import React, { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Space,
  Table,
  Typography,
  Upload,
  message
} from 'antd'
import { InboxOutlined, UploadOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import {
  applySeedImport,
  previewSeedImport,
  SeedPreview
} from '../../api/admin'

const { Title, Paragraph, Text } = Typography
const { Dragger } = Upload

type FileKey = 'users' | 'orders' | 'votes'

const fileLabels: Record<FileKey, string> = {
  users: 'users.csv（必填）',
  orders: 'orders.csv（可选）',
  votes: 'votes.csv（可选）'
}

const SeedImport: React.FC = () => {
  const [files, setFiles] = useState<Partial<Record<FileKey, File>>>({})
  const [preview, setPreview] = useState<SeedPreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [importLoading, setImportLoading] = useState(false)

  const canPreview = !!files.users

  const uploadProps = (key: FileKey) => ({
    maxCount: 1,
    accept: '.csv',
    beforeUpload: (file: File) => {
      setFiles((prev) => ({ ...prev, [key]: file }))
      setPreview(null)
      return false
    },
    onRemove: () => {
      setFiles((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      setPreview(null)
    },
    fileList: files[key]
      ? [{ uid: key, name: files[key]!.name, status: 'done' } as UploadFile]
      : []
  })

  const buildFormData = () => {
    const form = new FormData()
    if (files.users) form.append('users', files.users)
    if (files.orders) form.append('orders', files.orders)
    if (files.votes) form.append('votes', files.votes)
    return form
  }

  const handlePreview = async () => {
    if (!files.users) {
      message.warning('请先上传 users.csv')
      return
    }
    setPreviewLoading(true)
    try {
      const data = await previewSeedImport(buildFormData())
      setPreview(data)
      if (data.valid) {
        message.success('校验通过，可以导入')
      } else {
        message.error('校验未通过，请根据错误信息修改 CSV')
      }
    } catch {
      // 拦截器已提示
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleImport = async () => {
    if (!preview?.valid) {
      message.warning('请先预览并通过校验')
      return
    }
    setImportLoading(true)
    try {
      const result = await applySeedImport(buildFormData())
      message.success(
        `导入完成：用户 ${result.users}，审批单 ${result.orders}，投票新增 ${result.votes_added}、跳过重复 ${result.votes_skipped}`
      )
    } catch {
      // 拦截器已提示
    } finally {
      setImportLoading(false)
    }
  }

  const userColumns = useMemo(
    () => [
      { title: 'openid', dataIndex: 'openid', key: 'openid' },
      { title: '昵称', dataIndex: 'nickname', key: 'nickname' },
      { title: '审批币', dataIndex: 'coins', key: 'coins' }
    ],
    []
  )

  const orderColumns = useMemo(
    () => [
      { title: 'order_key', dataIndex: 'order_key', key: 'order_key' },
      { title: '发起人', dataIndex: 'owner_openid', key: 'owner_openid' },
      { title: '商品', dataIndex: 'item_name', key: 'item_name' },
      { title: '理由摘要', dataIndex: 'description', key: 'description' },
      { title: '价格(元)', dataIndex: 'price_yuan', key: 'price_yuan' },
      { title: '状态', dataIndex: 'status', key: 'status' }
    ],
    []
  )

  const voteColumns = useMemo(
    () => [
      { title: 'order_key', dataIndex: 'order_key', key: 'order_key' },
      { title: '投票人', dataIndex: 'voter_openid', key: 'voter_openid' },
      { title: '票型', dataIndex: 'vote_type', key: 'vote_type' },
      { title: '短评', dataIndex: 'comment', key: 'comment' }
    ],
    []
  )

  return (
    <div>
      <Title level={4}>演示数据导入</Title>
      <Paragraph type="secondary">
        上传与命令行脚本相同的三个 CSV（规范见仓库{' '}
        <Text code>backend/scripts/seed_csv_example/SEED_DATA_SPEC.md</Text>
        ）。建议 openid 使用 <Text code>test_seed_</Text> 前缀，便于 SQL 清理。
      </Paragraph>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {(Object.keys(fileLabels) as FileKey[]).map((key) => (
          <Card key={key} size="small" title={fileLabels[key]}>
            <Dragger {...uploadProps(key)}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽 CSV 到此处</p>
            </Dragger>
          </Card>
        ))}

        <Space>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            loading={previewLoading}
            disabled={!canPreview}
            onClick={handlePreview}
          >
            预览校验
          </Button>
          <Button
            type="primary"
            danger
            loading={importLoading}
            disabled={!preview?.valid}
            onClick={handleImport}
          >
            确认导入
          </Button>
        </Space>

        {preview && (
          <>
            <Alert
              type={preview.valid ? 'success' : 'error'}
              showIcon
              message={
                preview.valid
                  ? `校验通过：用户 ${preview.users_count}，审批单 ${preview.orders_count}，投票 ${preview.votes_count}`
                  : '校验未通过'
              }
              description={
                preview.errors.length > 0 ? (
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    {preview.errors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                ) : null
              }
            />
            {preview.warnings.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message="提示"
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    {preview.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                }
              />
            )}

            {preview.users_sample.length > 0 && (
              <Card title={`用户预览（共 ${preview.users_count} 条）`} size="small">
                <Table
                  rowKey="openid"
                  size="small"
                  pagination={false}
                  columns={userColumns}
                  dataSource={preview.users_sample}
                />
              </Card>
            )}

            {preview.orders_sample.length > 0 && (
              <Card title={`审批单预览（共 ${preview.orders_count} 条）`} size="small">
                <Table
                  rowKey="order_key"
                  size="small"
                  pagination={false}
                  columns={orderColumns}
                  dataSource={preview.orders_sample}
                />
              </Card>
            )}

            {preview.votes_sample.length > 0 && (
              <Card title={`投票预览（共 ${preview.votes_count} 条）`} size="small">
                <Table
                  rowKey={(r) => `${r.order_key}-${r.voter_openid}`}
                  size="small"
                  pagination={false}
                  columns={voteColumns}
                  dataSource={preview.votes_sample}
                />
              </Card>
            )}
          </>
        )}
      </Space>
    </div>
  )
}

export default SeedImport
